import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';

import { getRoomDeviceLogs } from '@src/api/room';

// This dialog can be opened from a lazy-loaded route (DynamicFloor) that never
// imports BookingCard, so we must extend dayjs with the timezone plugins here —
// otherwise utcOffset()/tz() are no-ops and times render in the browser's zone.
dayjs.extend(utc);
dayjs.extend(timezone);

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DATE_FMT = 'YYYY-MM-DD';

interface RoomCalendarDialogProps {
  open: boolean;
  onClose: () => void;
  roomNumber?: string | number | null;
  roomId?: string | null; // DB id of the room — device logs link by this (log.id === room.id)
  timeZone?: string | null; // room location offset e.g. '-07:00'; segment times are shown in it
  initialDate?: string | null; // 'YYYY-MM-DD'; opens the calendar on that date's month
}

interface Occupancy {
  id: string;
  date: string; // 'YYYY-MM-DD'
  startMin: number; // minutes from midnight (in the room's timezone)
  endMin: number;
}

// Monday of the week containing `date` (grid starts on Monday).
const mondayOf = (date: Dayjs) => date.subtract((date.day() + 6) % 7, 'day').startOf('day');

const formatMinutes = (minutes: number) => dayjs().startOf('day').add(minutes, 'minute').format('h:mm A');

// '+HH:mm' / '-HH:mm' timezone offset → minutes (matches the Scheduling page).
const parseOffsetMinutes = (offset?: string | null): number => {
  if (!offset) return 0;

  const match = offset.match(/^([+-])(\d{2}):(\d{2})$/);

  if (!match) return 0;

  const sign = match[1] === '-' ? -1 : 1;

  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
};

// Convert an epoch timestamp to the room's local time. `tz` may be a '+HH:mm'
// offset (as the Scheduling page uses) or an IANA name ('America/Los_Angeles').
// When it is missing or unparseable we fall back to the viewer's local zone —
// better than silently assuming UTC, which looks plausible but is wrong.
const toRoomLocal = (ts: number, tz?: string | null): Dayjs => {
  if (tz) {
    if (/^[+-]\d{2}:\d{2}$/.test(tz)) return dayjs(ts).utcOffset(parseOffsetMinutes(tz));

    try {
      const zoned = dayjs(ts).tz(tz);

      if (zoned.isValid()) return zoned;
    } catch {
      /* unknown IANA name — fall through to the viewer's local zone */
    }
  }

  return dayjs(ts);
};

// Device logs carry the room's DB id as `log.id` (the Scheduling page matches via
// results.id === log.id). Match on that first; fall back to a room number if present.
const matchesLog = (log: Record<string, any>, roomId?: string | null, roomNumber?: string | number | null): boolean => {
  if (!log) return false;

  if (roomId) {
    const ids = [log.id, log._id, log.roomId].filter(v => v !== undefined && v !== null).map(String);

    if (ids.includes(String(roomId))) return true;
  }

  if (roomNumber === null || roomNumber === undefined) return false;

  const target = String(roomNumber).trim().toLowerCase();

  const candidates = [log.roomNumber, log.room?.roomNumber, log.room?.name]
    .filter(v => v !== undefined && v !== null)
    .map(v => String(v).trim().toLowerCase());

  // Exact match only — substring matching conflated room '1' with '10', '100', …
  return candidates.some(v => v === target);
};

const csvCell = (value: string | number) => {
  let str = String(value ?? '');

  // Neutralize CSV formula injection — Excel/Sheets execute cells starting with
  // =, +, -, @ (or a leading tab/CR) as formulas; prefix with ' to defuse them.
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;

  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const RoomCalendarDialog = ({ open, onClose, roomNumber, roomId, timeZone, initialDate }: RoomCalendarDialogProps) => {
  const [monthStart, setMonthStart] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [occupancy, setOccupancy] = useState<Occupancy[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null); // drill-down day

  // Open on the month of the provided date (e.g. the schedule's selected date),
  // falling back to the current month; reset the drill-down each time it opens.
  useEffect(() => {
    if (open) {
      const base = initialDate && dayjs(initialDate).isValid() ? dayjs(initialDate) : dayjs();

      setMonthStart(base.startOf('month'));
      setSelectedDate(null);
    }
  }, [open, roomNumber, roomId, initialDate]);

  const fetchMonth = useCallback(async () => {
    if (!open || ((roomNumber === null || roomNumber === undefined) && !roomId)) return;

    setLoading(true);

    try {
      // One range call for the whole month (backend returns every day's segments,
      // each tagged with its room-local `date`).
      const logs: any[] = await getRoomDeviceLogs({
        startDate: monthStart.format(DATE_FMT),
        endDate: monthStart.endOf('month').format(DATE_FMT),
        ...(roomId ? { roomId } : {}), // let the backend process just this room
      })
        .then(res => res?.results || [])
        .catch(() => []);

      // The endpoint can return more than one log object for the room (e.g. one
      // per day/device) — collect segments from every matching log, not just the first.
      const matchingLogs = logs.filter(l => matchesLog(l, roomId, roomNumber));
      const collected: Occupancy[] = [];

      matchingLogs.forEach((log, logIdx) => {
        (log?.deviceOnSegments || []).forEach((seg: { fromTs: number; toTs: number }, idx: number) => {
          const start = toRoomLocal(seg.fromTs, timeZone);
          const end = toRoomLocal(seg.toTs, timeZone);

          if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return;

          // Split a segment that crosses midnight into per-day pieces so occupancy
          // spanning midnight is recorded on each day it touches.
          let cursor = start;

          while (cursor.isBefore(end)) {
            const dayEnd = cursor.endOf('day');
            const pieceEnd = end.isBefore(dayEnd) ? end : dayEnd;
            const date = cursor.format(DATE_FMT);
            const startMin = cursor.hour() * 60 + cursor.minute();
            // endOf('day') is 23:59:59.999 — treat a piece that runs to it as ending at 24:00.
            const endMin = pieceEnd.isSame(dayEnd) ? 24 * 60 : pieceEnd.hour() * 60 + pieceEnd.minute();

            if (endMin > startMin) collected.push({ id: `${logIdx}-${idx}-${date}`, date, startMin, endMin });

            cursor = dayEnd.add(1, 'millisecond'); // advance to the next day's 00:00
          }
        });
      });

      collected.sort((a, b) => a.date.localeCompare(b.date) || a.startMin - b.startMin);
      setOccupancy(collected);
    } finally {
      setLoading(false);
    }
  }, [open, roomNumber, roomId, timeZone, monthStart]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  // Group occupancy by date for quick per-cell lookup.
  const byDate = useMemo(() => {
    const map: Record<string, Occupancy[]> = {};

    occupancy.forEach(o => {
      (map[o.date] ||= []).push(o);
    });

    return map;
  }, [occupancy]);

  // Build the 7-column grid: full weeks (Mon-start) spanning the month.
  const gridDays = useMemo(() => {
    const first = mondayOf(monthStart);
    const lastOfMonth = monthStart.endOf('month');
    const cells: Dayjs[] = [];
    let cursor = first;

    while (cursor.isBefore(lastOfMonth) || cells.length % 7 !== 0) {
      cells.push(cursor);
      cursor = cursor.add(1, 'day');
    }

    return cells;
  }, [monthStart]);

  const today = dayjs().format(DATE_FMT);

  const exportCsv = () => {
    const header = ['Room', 'Date', 'Day', 'Start', 'End', 'Duration (min)', 'Status'];

    const rows = occupancy.map(o => [
      String(roomNumber ?? ''),
      o.date,
      dayjs(o.date).format('ddd'),
      formatMinutes(o.startMin),
      formatMinutes(o.endMin),
      o.endMin - o.startMin,
      'Occupied',
    ]);

    const csv = [header, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
    // Prepend a UTF-8 BOM so Excel opens the file with correct encoding.
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const safeRoom = String(roomNumber ?? 'unknown').replace(/[^a-z0-9_-]+/gi, '_');

    link.href = url;
    link.download = `room-${safeRoom}-occupancy-${monthStart.format('YYYY-MM')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const detailOccupancy = selectedDate ? byDate[selectedDate] || [] : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pr: 7 }}
      >
        <span style={{ fontWeight: 700 }}>Room {roomNumber ?? ''} · Occupancy</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconButton size='small' onClick={() => setMonthStart(m => m.subtract(1, 'month'))}>
            <ChevronLeftIcon />
          </IconButton>
          <span style={{ fontSize: 15, fontWeight: 600, minWidth: 150, textAlign: 'center' }}>
            {monthStart.format('MMMM YYYY')}
          </span>
          <IconButton size='small' onClick={() => setMonthStart(m => m.add(1, 'month'))}>
            <ChevronRightIcon />
          </IconButton>
          <Button size='small' variant='outlined' onClick={() => setMonthStart(dayjs().startOf('month'))}>
            Today
          </Button>
          <Button
            size='small'
            variant='contained'
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={exportCsv}
            disabled={occupancy.length === 0}
          >
            Export
          </Button>
        </span>
      </DialogTitle>

      <IconButton aria-label='close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'black' }}>
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        {selectedDate ? (
          /* ---------- DAY DETAIL (drill-down) ---------- */
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Button size='small' startIcon={<ChevronLeftIcon />} onClick={() => setSelectedDate(null)}>
                Back to month
              </Button>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{dayjs(selectedDate).format('dddd, MMM D, YYYY')}</span>
            </div>

            {detailOccupancy.length === 0 ? (
              <div style={{ color: '#888', padding: '12px 0' }}>No occupancy recorded for this day.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {detailOccupancy.map(o => (
                  <div
                    key={o.id}
                    style={{
                      display: 'flex',
                      gap: 12,
                      border: '1px solid #eee',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: '#fff',
                    }}
                  >
                    <div style={{ width: 6, background: '#e53935', flexShrink: 0 }} />
                    <div style={{ padding: '10px 4px' }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {formatMinutes(o.startMin)} – {formatMinutes(o.endMin)}
                      </div>
                      <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>
                        Occupied · {o.endMin - o.startMin} min
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* ---------- MONTH GRID ---------- */
          <div style={{ position: 'relative' }}>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.6)',
                  zIndex: 5,
                }}
              >
                <CircularProgress size={28} />
              </div>
            )}

            {/* Weekday header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
              {DAY_LABELS.map(label => (
                <div key={label} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#888' }}>
                  {label}
                </div>
              ))}
            </div>

            {/* Day cells */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
              {gridDays.map(day => {
                const dateStr = day.format(DATE_FMT);
                const inMonth = day.month() === monthStart.month();
                const isToday = dateStr === today;
                const dayOcc = byDate[dateStr] || [];

                return (
                  <div
                    key={dateStr}
                    onClick={() => inMonth && setSelectedDate(dateStr)}
                    style={{
                      minHeight: 96,
                      border: isToday ? '2px solid #406f81' : '1px solid #e8e8e8',
                      borderRadius: 6,
                      padding: 4,
                      background: inMonth ? '#fff' : '#fafafa',
                      opacity: inMonth ? 1 : 0.45,
                      cursor: inMonth ? 'pointer' : 'default',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textAlign: 'right' }}>
                      {day.format('D')}
                    </div>

                    {dayOcc.slice(0, 3).map(o => (
                      <div
                        key={o.id}
                        title={`Occupied · ${formatMinutes(o.startMin)} – ${formatMinutes(o.endMin)}`}
                        style={{
                          background: '#e53935',
                          color: '#fff',
                          borderRadius: 4,
                          padding: '2px 4px',
                          fontSize: 10,
                          lineHeight: 1.2,
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{formatMinutes(o.startMin)}</div>
                        <div>{formatMinutes(o.endMin)}</div>
                      </div>
                    ))}

                    {dayOcc.length > 3 && (
                      <div style={{ fontSize: 10, color: '#888', textAlign: 'center' }}>+{dayOcc.length - 3} more</div>
                    )}
                  </div>
                );
              })}
            </div>

            {!loading && occupancy.length === 0 && (
              <div style={{ textAlign: 'center', color: '#888', padding: '16px 0 4px' }}>
                No occupancy recorded for this room in {monthStart.format('MMMM YYYY')}.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoomCalendarDialog;
